import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
    REFINE_GENERATED_IMAGE,
    REFINE_GENERATED_VIDEO,
    GENERATED_IMAGE_STATUS,
    POLL_GENERATED_VIDEOS,
} from '@/graphql/ads/generated-image-queries'

export type RefineMediaType = 'IMAGE' | 'VIDEO'
export type RefineStatus = 'idle' | 'sending' | 'processing' | 'failed'

export interface GeneratedAsset {
    id: string
    url: string | null
    prompt: string | null
    format: string
    status: string
    error: string | null
    name?: string | null
}

/** One turn of the thread: what the user asked for (empty when they only edited the prompt) and the prompt sent */
export interface RefineTurn {
    feedback: string
    prompt: string
    failed?: boolean
}

/** What a turn sends: feedback for the model to revise the prompt from, the prompt as the user edited it, or both */
export interface RefineRequest {
    feedback?: string
    prompt?: string
}

interface UseRefineMediaOptions {
    mediaType: RefineMediaType
    asset: GeneratedAsset
    campaignId?: string | null
    /** Called once with each regenerated asset that completed, for the screen to put it in the source's place */
    onGenerated?: (asset: GeneratedAsset) => void
}

interface RefinedImageData {
    refineGeneratedImage: { revisedPrompt: string; image: GeneratedAsset }
}

interface RefinedVideoData {
    refineGeneratedVideo: { revisedPrompt: string; video: GeneratedAsset }
}

const POLL_INTERVAL_MS = 3000
const FAILED_MESSAGE = 'The generation failed.'
const NOTHING_TO_SEND = 'Say what should change or edit the prompt.'

const messageOf = (error: unknown): string => {
    const graphQLErrors = (error as { graphQLErrors?: { message?: string }[] })?.graphQLErrors
    return graphQLErrors?.[0]?.message || (error as Error)?.message || 'Something went wrong. Please try again.'
}

/**
 * The thread a refine dialog keeps while it is open: the current asset, the turns so far, and the state of the
 * turn in flight. `send` starts one regeneration from the feedback (the backend revises the prompt from it and
 * the earlier turns), from the prompt as the user edited it (used as it is), or from both (the feedback revises
 * the edited prompt), then polls its record until it completes (the new asset becomes current and `onGenerated`
 * gets it) or fails (the previous asset stays current and the turn carries the error). `pending` is the record
 * still generating, for the screen to keep tracking when the dialog closes mid-turn. Closing the dialog drops
 * the thread.
 */
export const useRefineMedia = ({ mediaType, asset, campaignId, onGenerated }: UseRefineMediaOptions) => {
    const isVideo = mediaType === 'VIDEO'
    const [current, setCurrent] = useState<GeneratedAsset>(asset)
    const [turns, setTurns] = useState<RefineTurn[]>([])
    const [status, setStatus] = useState<RefineStatus>('idle')
    const [error, setError] = useState<string | null>(null)
    const [pending, setPending] = useState<GeneratedAsset | null>(null)
    const pendingId = pending?.id ?? null

    const [refineImage] = useMutation<RefinedImageData>(REFINE_GENERATED_IMAGE)
    const [refineVideo] = useMutation<RefinedVideoData>(REFINE_GENERATED_VIDEO)

    const { data: imageStatusData } = useQuery<{ generatedImageStatus: GeneratedAsset | null }>(
        GENERATED_IMAGE_STATUS,
        {
            variables: { id: pendingId },
            skip: isVideo || !pendingId,
            pollInterval: POLL_INTERVAL_MS,
            fetchPolicy: 'network-only',
        },
    )
    const { data: videoStatusData } = useQuery<{ generatedVideosByIds: GeneratedAsset[] }>(POLL_GENERATED_VIDEOS, {
        variables: { ids: [pendingId] },
        skip: !isVideo || !pendingId,
        pollInterval: POLL_INTERVAL_MS,
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        const record = isVideo ? videoStatusData?.generatedVideosByIds?.[0] : imageStatusData?.generatedImageStatus
        if (!pendingId || !record || record.id !== pendingId) return

        if (record.status === 'completed') {
            setPending(null)
            setCurrent(record)
            setStatus('idle')
            onGenerated?.(record)
        } else if (record.status === 'failed') {
            setPending(null)
            setStatus('failed')
            setError(record.error || FAILED_MESSAGE)
            setTurns((prev) =>
                prev.map((turn, index) => (index === prev.length - 1 ? { ...turn, failed: true } : turn)),
            )
        }
    }, [imageStatusData, videoStatusData, pendingId, isVideo, onGenerated])

    const send = async (request: RefineRequest) => {
        const feedback = request.feedback?.trim() ?? ''
        const prompt = request.prompt?.trim() ?? ''
        if (!feedback && !prompt) {
            setStatus('failed')
            setError(NOTHING_TO_SEND)
            return
        }
        setStatus('sending')
        setError(null)
        const history = turns.map(({ feedback: turnFeedback, prompt: turnPrompt }) => ({
            feedback: turnFeedback,
            prompt: turnPrompt,
        }))
        const input = {
            [isVideo ? 'sourceVideoId' : 'sourceImageId']: current.id,
            ...(feedback ? { feedback } : {}),
            ...(prompt ? { prompt } : {}),
            history,
            ...(campaignId ? { campaignId } : {}),
        }

        try {
            let revisedPrompt: string
            let record: GeneratedAsset
            if (isVideo) {
                const { data } = await refineVideo({ variables: { input } })
                const result = data?.refineGeneratedVideo
                if (!result) throw new Error(FAILED_MESSAGE)
                revisedPrompt = result.revisedPrompt
                record = result.video
            } else {
                const { data } = await refineImage({ variables: { input } })
                const result = data?.refineGeneratedImage
                if (!result) throw new Error(FAILED_MESSAGE)
                revisedPrompt = result.revisedPrompt
                record = result.image
            }
            setTurns((prev) => [...prev, { feedback, prompt: revisedPrompt }])
            setPending(record)
            setStatus('processing')
        } catch (mutationError) {
            setStatus('failed')
            setError(messageOf(mutationError))
        }
    }

    return { current, turns, status, error, pending, send }
}
